import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import LoginPage from '@/pages/LoginPage';
import AuthCallback from '@/pages/AuthCallback';
import Layout from '@/pages/Layout';
import InstructorDashboard from '@/pages/InstructorDashboard';
import ParentDashboard from '@/pages/ParentDashboard';
import SessionPage from '@/pages/SessionPage';
import StoryboardPage from '@/pages/StoryboardPage';
import AssessmentPage from '@/pages/AssessmentPage';
import ProgressPage from '@/pages/ProgressPage';
import CriteriaPage from '@/pages/CriteriaPage';
import MonthlyReportsPage from '@/pages/MonthlyReportsPage';
import SessionComparePage from '@/pages/SessionComparePage';
import AccountSettingsPage from '@/pages/AccountSettingsPage';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminChildDetailPage from '@/pages/AdminChildDetailPage';
import InstructorHoursPage from '@/pages/InstructorHoursPage';
import MessagesPage from '@/pages/MessagesPage';
import SchedulePage from '@/pages/SchedulePage';
import AIInsightsPage from '@/pages/AIInsightsPage';
import KnowledgeLibraryPage from '@/pages/KnowledgeLibraryPage';
import AIProgressReportPage from '@/pages/AIProgressReportPage';
import IntakeFormPage from '@/pages/IntakeFormPage';
import PracticeVideosPage from '@/pages/PracticeVideosPage';
import ExpensesPage from '@/pages/ExpensesPage';
import FacilityDashboard from '@/pages/FacilityDashboard';
import { Toaster } from '@/components/ui/sonner';
import { Waves } from 'lucide-react';
import '@/App.css';

function LoadingScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F0FDFA]" data-testid="loading-screen">
      <img src="/logo.png" alt="Big Effort Swim" className="w-24 h-24 mb-4 animate-pulse" />
      <h1 className="text-3xl font-extrabold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>Big Effort Swim</h1>
      <p className="text-slate-500 mt-2">Loading...</p>
    </div>
  );
}

function ChildSelector({ basePath, label }) {
  const [children, setChildren] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const res = await api.get('/children');
        setChildren(res.data);
        if (res.data.length === 1) {
          navigate(`${basePath}/${res.data[0].child_id}`, { replace: true });
        }
      } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
    };
    fetchChildren();
  }, [navigate, basePath]);

  if (children.length === 1) return <div className="flex items-center justify-center p-8"><img src="/logo.png" alt="Loading" className="w-10 h-10 animate-pulse" /></div>;

  return (
    <div className="max-w-2xl mx-auto" data-testid="child-selector">
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Nunito' }}>{label}</h2>
      <div className="grid gap-4">
        {children.map(c => (
          <button
            key={c.child_id}
            onClick={() => navigate(`${basePath}/${c.child_id}`)}
            className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md hover:border-[#0EA5E9]/30 transition-all text-left flex items-center gap-4"
            data-testid={`select-child-${c.child_id}`}
          >
            <div className="w-12 h-12 rounded-2xl bg-[#ECFEFF] flex items-center justify-center text-[#22D3EE] font-bold text-xl" style={{ fontFamily: 'Nunito' }}>
              {c.name.charAt(0)}
            </div>
            <div>
              <p className="font-bold text-slate-800">{c.name}</p>
              <p className="text-sm text-slate-500">{c.notes || 'No notes'}</p>
            </div>
          </button>
        ))}
        {children.length === 0 && <p className="text-slate-400 text-center p-8">No children found</p>}
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();

  // Handle OAuth callback on any route (hash or query param)
  const hasSessionInHash = location.hash?.includes('session_id=');
  const hasSessionInQuery = location.search?.includes('session_id=');
  if (hasSessionInHash || hasSessionInQuery || location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  if (loading) return <LoadingScreen />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={user ? <Layout /> : <Navigate to="/login" />}>
        <Route index element={user?.role === 'instructor' ? <InstructorDashboard /> : user?.role === 'facility_manager' ? <FacilityDashboard /> : <ParentDashboard />} />
        <Route path="session/:sessionId" element={<SessionPage />} />
        <Route path="storyboard/select" element={<ChildSelector basePath="/dashboard/storyboard" label="Select child for Storyboard" />} />
        <Route path="storyboard/:childId" element={<StoryboardPage />} />
        <Route path="assessment/:sessionId" element={<AssessmentPage />} />
        <Route path="progress/select" element={<ChildSelector basePath="/dashboard/progress" label="Select child for Progress" />} />
        <Route path="progress/:childId" element={<ProgressPage />} />
        <Route path="criteria/select" element={<ChildSelector basePath="/dashboard/criteria" label="Select child for Criteria" />} />
        <Route path="criteria/:childId" element={<CriteriaPage />} />
        <Route path="reports/select" element={<ChildSelector basePath="/dashboard/reports" label="Select child for Reports" />} />
        <Route path="reports/:childId" element={<MonthlyReportsPage />} />
        <Route path="compare/:childId" element={<SessionComparePage />} />
        <Route path="feedback/select" element={<ChildSelector basePath="/dashboard/feedback" label="Select child for Feedback" />} />
        <Route path="feedback/:childId" element={<ParentDashboard />} />
        <Route path="settings" element={<AccountSettingsPage />} />
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="admin/child/:childId" element={<AdminChildDetailPage />} />
        <Route path="hours" element={<InstructorHoursPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="schedule" element={<SchedulePage />} />
        <Route path="ai-insights/select" element={<ChildSelector basePath="/dashboard/ai-insights" label="Select child for AI Insights" />} />
        <Route path="ai-insights/:childId" element={<AIInsightsPage />} />
        <Route path="ai-progress/:childId" element={<AIProgressReportPage />} />
        <Route path="intake/:childId" element={<IntakeFormPage />} />
        <Route path="knowledge" element={<KnowledgeLibraryPage />} />
        <Route path="practice-videos/select" element={<ChildSelector basePath="/dashboard/practice-videos" label="Select child for Practice Videos" />} />
        <Route path="practice-videos/:childId" element={<PracticeVideosPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="facility" element={<FacilityDashboard />} />
      </Route>
      <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
